/**
 * Marketing → Social Media Planner (Rella-style upgrade)
 *
 * Four views:
 *   - Month  (drag-drop calendar, one card per day)
 *   - Week   (same grid, 7-day slice)
 *   - Board  (columns per assignee — see user screenshot)
 *   - Table  (sortable rows, quick-edit)
 *
 * Each post supports:
 *   - Multiple platforms (Facebook + Instagram at once)
 *   - Extended statuses (idea / in_progress / pending_review / approved / scheduled / published / archived)
 *   - Assignee (Peter / Sabrina / Larkin / any team member)
 *   - Attachments (Reels, images, PDFs — up to 8 files, ≤10 MB each)
 *   - Private notes/comments (Rella "Activity → Private" tab)
 *   - Timestamped history (who changed what, when)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Loader2, Send, Paperclip, X as XIcon, MessageSquare, History as HistoryIcon, Trash2, FileText, ExternalLink, Facebook, Instagram, Linkedin, Youtube, Users as UsersIcon, Image as ImageIcon, Video as VideoIcon, PenTool } from 'lucide-react';
import { toast } from 'sonner';

export const SOCIAL_STATUSES = ['idea', 'in_progress', 'pending_review', 'approved', 'scheduled', 'published', 'archived'];

export const POST_TYPE_META = {
  photo:   { label: 'PHOTO',   Icon: ImageIcon, bg: '#E1F5FE', text: '#01579B' },
  video:   { label: 'VIDEO',   Icon: VideoIcon, bg: '#FCE4EC', text: '#880E4F' },
  graphic: { label: 'GRAPHIC', Icon: PenTool,   bg: '#F3E5F5', text: '#4A148C' },
};

// Rella-style assignee palette (Larkin=yellow, Peter=purple, Sabrina=pink…)
export const TEAM_COLORS = [
  { name: 'Yellow', hex: '#F5C842' },
  { name: 'Purple', hex: '#9B59D0' },
  { name: 'Pink',   hex: '#F06CA8' },
  { name: 'Blue',   hex: '#4A90E2' },
  { name: 'Green',  hex: '#3FA96B' },
  { name: 'Orange', hex: '#F08A3C' },
  { name: 'Teal',   hex: '#2BB5B0' },
  { name: 'Red',    hex: '#E05252' },
];

const STATUS_META = {
  idea:             { label: 'Idea',             color: '#6B7280', bg: '#F3F4F6', text: '#374151' },
  in_progress:      { label: 'In progress',      color: '#6B3FA0', bg: '#EDE7F6', text: '#4A148C' },
  pending_review:   { label: 'Pending review',   color: '#E65100', bg: '#FFF3E0', text: '#B45309' },
  approved:         { label: 'Approved',         color: '#0277BD', bg: '#E1F5FE', text: '#01579B' },
  scheduled:        { label: 'Scheduled',        color: '#1B5E20', bg: '#E8F5E9', text: '#1B5E20' },
  published:        { label: 'Published',        color: '#2E7D32', bg: '#DCEDC8', text: '#33691E' },
  archived:         { label: 'Archived',         color: '#9E9E9E', bg: '#F5F5F5', text: '#616161' },
};

const PLATFORM_META = {
  facebook:  { label: 'Facebook',  color: '#1877F2', Icon: Facebook },
  instagram: { label: 'Instagram', color: '#E4405F', Icon: Instagram },
  linkedin:  { label: 'LinkedIn',  color: '#0A66C2', Icon: Linkedin },
  youtube:   { label: 'YouTube',   color: '#FF0000', Icon: Youtube },
  twitter:   { label: 'X/Twitter', color: '#1DA1F2', Icon: null },
  tiktok:    { label: 'TikTok',    color: '#000000', Icon: null },
  other:     { label: 'Other',     color: '#6B7280', Icon: null },
};

const MAX_UPLOAD_BYTES = 300 * 1024 * 1024;   // 300 MB — Reels & hi-res graphics
const UPLOAD_CHUNK_BYTES = 4 * 1024 * 1024;   // 4 MB chunks through the proxy
const MAX_ATTACHMENTS = 8;

function PostTypeChip({ type, size = 'sm' }) {
  const meta = POST_TYPE_META[type];
  if (!meta) return null;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded font-bold ${size === 'lg' ? 'text-[10px] px-1.5 py-0.5' : 'text-[8px] px-1 py-px'}`}
          style={{ backgroundColor: meta.bg, color: meta.text }}>
      <Icon className={size === 'lg' ? 'h-3 w-3' : 'h-2.5 w-2.5'} />{meta.label}
    </span>
  );
}

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const platformsOf = (p) => (p.platforms && p.platforms.length) ? p.platforms : (p.platform ? [p.platform] : []);

function PlatformChips({ platforms, size = 'sm' }) {
  const s = size === 'lg' ? 'h-5 px-2 text-[11px]' : 'h-4 px-1.5 text-[10px]';
  return (
    <div className="flex flex-wrap gap-1">
      {(platforms || []).map(pl => {
        const meta = PLATFORM_META[pl] || PLATFORM_META.other;
        const Icon = meta.Icon;
        return (
          <span key={pl}
                className={`inline-flex items-center gap-1 rounded ${s} text-white font-medium`}
                style={{ backgroundColor: meta.color }}
                title={meta.label}>
            {Icon && <Icon className="h-3 w-3" />}
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.idea;
  return (
    <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5"
          style={{ backgroundColor: meta.bg, color: meta.text }}>
      {meta.label}
    </span>
  );
}

export default function SocialTab({ api }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [team, setTeam] = useState([]);           // [{employee_id, name, color}]
  const [teamOpen, setTeamOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');      // month | week | board | table
  const [cursor, setCursor] = useState(new Date());
  const [editing, setEditing] = useState(null);   // post being edited (null = closed, {} = new)
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/marketing/social'),
      api.get('/employees').catch(() => ({ data: [] })),
      api.get('/marketing/social/team').catch(() => ({ data: { members: [] } })),
    ]).then(([s, u, t]) => {
      setItems(s.data);
      setUsers(u.data || []);
      setTeam(t.data?.members || []);
    }).catch(() => toast.error('Load failed'))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(load, [load]);

  const colorOf = useCallback((assigneeId) => team.find(m => m.employee_id === assigneeId)?.color || '', [team]);

  const onDropOn = async (targetDate) => {
    if (!dragId) return;
    const post = items.find(p => p.id === dragId);
    if (!post) { setDragId(null); return; }
    const oldTime = (post.scheduled_date || '').slice(11) || '10:00';
    try {
      await api.put(`/marketing/social/${dragId}`, { scheduled_date: `${targetDate}T${oldTime}` });
      toast.success('Moved');
      load();
    } catch { toast.error('Move failed'); }
    setDragId(null);
  };

  const openNew = (dateStr) => {
    setEditing({
      scheduled_date: dateStr ? `${dateStr}T12:00` : `${isoDate(new Date())}T12:00`,
      platforms: ['facebook', 'instagram'],
      title: '', copy: '', status: 'in_progress',
      assignee_id: '', assignee_name: '',
      attachments: [],
    });
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E91E63]" /></div>;

  return (
    <div data-testid="mk-social">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <ViewSwitcher view={view} setView={setView} cursor={cursor} setCursor={setCursor} />
        <div className="flex items-center gap-2">
          {team.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-1" data-testid="mk-social-team-legend">
              {team.map(m => (
                <span key={m.employee_id} className="inline-flex items-center gap-1 text-[11px] text-gray-600">
                  <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ backgroundColor: m.color }} />
                  {m.name.split(/\s+/)[0]}
                </span>
              ))}
            </div>
          )}
          <Button variant="outline" onClick={() => setTeamOpen(true)} data-testid="mk-social-team-btn">
            <UsersIcon className="h-4 w-4 mr-1" /> Team
          </Button>
          <Button onClick={() => openNew()} className="bg-[#E91E63] hover:bg-[#C2185B] text-white" data-testid="mk-social-new">
            <Plus className="h-4 w-4 mr-1" /> Schedule post
          </Button>
        </div>
      </div>

      {view === 'month' && <MonthOrWeek items={items} cursor={cursor} onDropOn={onDropOn}
                                        onDragStart={setDragId} onDragEnd={() => setDragId(null)}
                                        onOpen={setEditing} onAdd={openNew} weekOnly={false} colorOf={colorOf} />}
      {view === 'week'  && <MonthOrWeek items={items} cursor={cursor} onDropOn={onDropOn}
                                        onDragStart={setDragId} onDragEnd={() => setDragId(null)}
                                        onOpen={setEditing} onAdd={openNew} weekOnly={true} colorOf={colorOf} />}
      {view === 'board' && <BoardView items={items} users={users} team={team} onOpen={setEditing} onAdd={openNew} colorOf={colorOf} />}
      {view === 'table' && <TableView items={items} onOpen={setEditing} colorOf={colorOf} />}

      {editing && (
        <PostEditor post={editing} api={api} users={users} team={team}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }} />
      )}
      {teamOpen && (
        <TeamManager api={api} users={users} team={team}
                     onClose={() => setTeamOpen(false)}
                     onSaved={(members) => { setTeam(members); setTeamOpen(false); }} />
      )}
    </div>
  );
}

function ViewSwitcher({ view, setView, cursor, setCursor }) {
  const label = view === 'week'
    ? weekLabel(cursor)
    : cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const jump = (dir) => {
    const d = new Date(cursor);
    if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {(view === 'month' || view === 'week') && (
        <>
          <Button variant="outline" size="sm" onClick={() => jump(-1)} data-testid="mk-social-prev">‹</Button>
          <div className="font-semibold text-lg min-w-[220px] text-center" data-testid="mk-social-label">{label}</div>
          <Button variant="outline" size="sm" onClick={() => jump(1)} data-testid="mk-social-next">›</Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="mk-social-today">Today</Button>
        </>
      )}
      <div className="flex gap-0 border rounded overflow-hidden ml-2">
        {[
          { k: 'month', label: 'Month' },
          { k: 'week',  label: 'Week' },
          { k: 'board', label: 'Board' },
          { k: 'table', label: 'Table' },
        ].map(v => (
          <button key={v.k} onClick={() => setView(v.k)}
                  className={`px-3 py-1.5 text-sm ${view === v.k ? 'bg-[#E91E63] text-white' : 'bg-white hover:bg-gray-50'}`}
                  data-testid={`mk-social-view-${v.k}`}>
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function weekLabel(cursor) {
  const start = new Date(cursor);
  start.setDate(start.getDate() - start.getDay());
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Week of ${fmt(start)} — ${fmt(end)}, ${end.getFullYear()}`;
}

function MonthOrWeek({ items, cursor, onDropOn, onDragStart, onDragEnd, onOpen, onAdd, weekOnly, colorOf }) {
  const cursorMonth = cursor.getMonth();
  const gridStart = new Date(cursor);
  if (weekOnly) {
    gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  } else {
    gridStart.setDate(1);
    gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  }
  const cellCount = weekOnly ? 7 : 42;
  const cells = [];
  for (let i = 0; i < cellCount; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  const postsByDay = items.reduce((acc, p) => {
    const k = (p.scheduled_date || '').slice(0, 10);
    if (k) (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});
  const today = isoDate(new Date());
  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid={`mk-social-${weekOnly ? 'week' : 'month'}`}>
      <div className="grid grid-cols-7 border-b bg-gray-50">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="px-2 py-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wider text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const key = isoDate(d);
          const inMonth = weekOnly || d.getMonth() === cursorMonth;
          const isToday = key === today;
          const dayPosts = postsByDay[key] || [];
          return (
            <div key={i}
                 className={`group border-r border-b p-1.5 flex flex-col gap-1 ${weekOnly ? 'min-h-[400px]' : 'min-h-[120px]'} ${inMonth ? 'bg-white' : 'bg-gray-50'} ${isToday ? 'ring-2 ring-inset ring-[#E91E63]' : ''}`}
                 onDragOver={(e) => e.preventDefault()}
                 onDrop={() => onDropOn(key)}
                 data-testid={`mk-social-day-${key}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${inMonth ? 'text-gray-800' : 'text-gray-400'} ${isToday ? 'text-[#E91E63]' : ''}`}>
                  {weekOnly ? d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }) : d.getDate()}
                </span>
                <button onClick={() => onAdd(key)}
                        className="opacity-0 group-hover:opacity-100 text-[10px] text-[#E91E63] hover:underline"
                        style={{ opacity: inMonth ? undefined : 0 }}
                        data-testid={`mk-social-add-${key}`}>+ post</button>
              </div>
              {dayPosts.map(p => (
                <PostChip key={p.id} post={p}
                          onOpen={() => onOpen(p)}
                          onDragStart={() => onDragStart(p.id)}
                          onDragEnd={onDragEnd}
                          size={weekOnly ? 'lg' : 'sm'}
                          color={colorOf ? colorOf(p.assignee_id) : ''} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PostChip({ post, onOpen, onDragStart, onDragEnd, size, color }) {
  const platforms = platformsOf(post);
  const status = STATUS_META[post.status] || STATUS_META.idea;
  return (
    <div draggable
         onDragStart={onDragStart}
         onDragEnd={onDragEnd}
         onClick={onOpen}
         className="rounded overflow-hidden cursor-grab active:cursor-grabbing border shadow-sm hover:shadow transition-shadow"
         style={{ backgroundColor: color ? `${color}1F` : '#fff', borderColor: color ? `${color}66` : undefined }}
         data-testid={`mk-social-card-${post.id}`}>
      {post.image_url && size === 'lg' && (
        <div className="aspect-square bg-gray-100">
          <img src={post.image_url} alt="" className="w-full h-full object-cover"
               onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      )}
      <div className="p-1.5 space-y-1" style={{ borderLeft: `3px solid ${color || status.color}` }}>
        <div className="flex items-center gap-1 flex-wrap">
          <PlatformChips platforms={platforms} />
          <PostTypeChip type={post.post_type} size={size} />
        </div>
        {post.title && <div className={`font-semibold ${size === 'lg' ? 'text-xs' : 'text-[10px]'} text-gray-800 line-clamp-2 leading-tight`}>{post.title}</div>}
        <div className={`text-gray-600 leading-tight line-clamp-2 ${size === 'lg' ? 'text-[11px]' : 'text-[10px]'}`}>{post.copy || '(no copy)'}</div>
        <div className="flex items-center gap-1 flex-wrap">
          <StatusBadge status={post.status} />
          {post.assignee_name && (
            <span className="text-[9px] text-gray-600 inline-flex items-center gap-0.5">
              {color && <span className="h-2 w-2 rounded-full inline-block" style={{ backgroundColor: color }} />}
              {post.assignee_name.split(/\s+/)[0]}
            </span>
          )}
          {(post.attachments || []).length > 0 && (
            <span className="text-[9px] text-gray-500 flex items-center gap-0.5"><Paperclip className="h-2.5 w-2.5" />{post.attachments.length}</span>
          )}
          {(post.comments || []).length > 0 && (
            <span className="text-[9px] text-gray-500 flex items-center gap-0.5"><MessageSquare className="h-2.5 w-2.5" />{post.comments.length}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function BoardView({ items, users, team, onOpen, onAdd, colorOf }) {
  // Group by assignee, plus one "Unassigned" column
  const byAssignee = {};
  const unassigned = [];
  for (const p of items) {
    if (p.assignee_id) {
      const key = p.assignee_id;
      byAssignee[key] = byAssignee[key] || { name: p.assignee_name || 'Unknown', posts: [] };
      byAssignee[key].posts.push(p);
    } else {
      unassigned.push(p);
    }
  }
  const columns = [];
  // Team roster columns first (always visible, in roster order, with colors)
  const roster = team.length ? team : users.map(u => ({ employee_id: u.id, name: u.name, color: '' }));
  for (const m of roster) {
    columns.push({ id: m.employee_id, label: m.name || 'Team', color: m.color || '', posts: byAssignee[m.employee_id]?.posts || [] });
  }
  // Any assignees on posts but not in the roster (old posts, deleted accounts)
  for (const [id, val] of Object.entries(byAssignee)) {
    if (!columns.some(c => c.id === id)) columns.push({ id, label: val.name, color: colorOf(id), posts: val.posts });
  }
  columns.push({ id: '__unassigned', label: 'Unassigned', color: '', posts: unassigned });
  const active = team.length
    ? columns.filter(c => c.posts.length > 0 || c.id === '__unassigned' || team.some(m => m.employee_id === c.id))
    : columns.filter(c => c.posts.length > 0 || c.id === '__unassigned');
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-testid="mk-social-board">
      {active.map(col => (
        <div key={col.id} className="min-w-[240px] w-72 shrink-0 rounded-lg p-2"
             style={{ backgroundColor: col.color ? `${col.color}1A` : '#F9FAFB' }}
             data-testid={`mk-social-board-col-${col.id}`}>
          <div className="flex items-center justify-between mb-2 px-1 rounded"
               style={col.color ? { backgroundColor: `${col.color}40`, padding: '4px 6px' } : {}}>
            <div className="font-semibold text-xs uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
              {col.color && <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ backgroundColor: col.color }} />}
              {col.label}
              <span className="text-gray-500 font-normal">({col.posts.length})</span>
            </div>
            <button onClick={() => onAdd()} className="text-[#E91E63] hover:underline text-[10px]"
                    data-testid={`mk-social-board-add-${col.id}`}>+</button>
          </div>
          <div className="space-y-2">
            {col.posts.map(p => <PostChip key={p.id} post={p} onOpen={() => onOpen(p)} size="lg" color={col.color || colorOf(p.assignee_id)} />)}
            {col.posts.length === 0 && <div className="text-[11px] text-gray-400 italic px-1">Nothing here yet.</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function TableTh({ col, sortBy, dir, onSort, children }) {
  return (
    <th onClick={() => onSort(col)}
        className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-gray-600 cursor-pointer hover:bg-gray-100"
        data-testid={`mk-social-table-sort-${col}`}>
      {children}{sortBy === col && (dir === 'asc' ? ' ▲' : ' ▼')}
    </th>
  );
}

function TableView({ items, onOpen, colorOf }) {
  const [sortBy, setSortBy] = useState('scheduled_date');
  const [dir, setDir] = useState('asc');
  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const av = (a[sortBy] ?? '') + '';
      const bv = (b[sortBy] ?? '') + '';
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [items, sortBy, dir]);
  const setSort = (col) => {
    if (sortBy === col) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setDir('asc'); }
  };
  return (
    <div className="border rounded-lg bg-white overflow-x-auto" data-testid="mk-social-table">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <TableTh col="scheduled_date" sortBy={sortBy} dir={dir} onSort={setSort}>When</TableTh>
            <TableTh col="title" sortBy={sortBy} dir={dir} onSort={setSort}>Title / Copy</TableTh>
            <TableTh col="status" sortBy={sortBy} dir={dir} onSort={setSort}>Status</TableTh>
            <TableTh col="post_type" sortBy={sortBy} dir={dir} onSort={setSort}>Type</TableTh>
            <TableTh col="assignee_name" sortBy={sortBy} dir={dir} onSort={setSort}>Assignee</TableTh>
            <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-gray-600">Platforms</th>
            <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-gray-600">Files</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(p => (
            <tr key={p.id} onClick={() => onOpen(p)}
                className="border-b hover:bg-gray-50 cursor-pointer"
                data-testid={`mk-social-table-row-${p.id}`}>
              <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(p.scheduled_date).toLocaleString()}</td>
              <td className="px-3 py-2 max-w-xs">
                {p.title && <div className="font-medium truncate">{p.title}</div>}
                <div className="text-xs text-gray-500 truncate">{p.copy}</div>
              </td>
              <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
              <td className="px-3 py-2"><PostTypeChip type={p.post_type} size="lg" /></td>
              <td className="px-3 py-2 text-xs">
                {p.assignee_name ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5"
                        style={colorOf(p.assignee_id) ? { backgroundColor: `${colorOf(p.assignee_id)}33` } : {}}>
                    {colorOf(p.assignee_id) && <span className="h-2 w-2 rounded-full inline-block" style={{ backgroundColor: colorOf(p.assignee_id) }} />}
                    {p.assignee_name}
                  </span>
                ) : '—'}
              </td>
              <td className="px-3 py-2"><PlatformChips platforms={platformsOf(p)} /></td>
              <td className="px-3 py-2 text-xs text-gray-500">
                {(p.attachments || []).length > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" />{p.attachments.length}</span>}
              </td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No posts yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PostEditor({ post, api, users, team, onClose, onSaved }) {
  const isNew = !post.id;
  const [form, setForm] = useState({
    scheduled_date: post.scheduled_date || `${isoDate(new Date())}T12:00`,
    platforms: platformsOf(post).length ? platformsOf(post) : ['facebook', 'instagram'],
    title: post.title || '',
    copy: post.copy || '',
    image_url: post.image_url || '',
    status: post.status || 'in_progress',
    post_type: post.post_type || '',
    assignee_id: post.assignee_id || '',
    assignee_name: post.assignee_name || '',
    attachments: post.attachments || [],
  });
  const [comments, setComments] = useState(post.comments || []);
  const [history, setHistory] = useState(post.history || []);
  const [newComment, setNewComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState([]);   // [{name, pct}] in-flight

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Assignee options: the social team roster (Larkin/Peter/Sabrina/Josh…) if
  // configured; falls back to everyone until a roster is saved.
  const assigneeOptions = team.length
    ? team.map(m => ({ id: m.employee_id, name: m.name }))
    : users.map(u => ({ id: u.id, name: u.name }));
  // Keep a legacy assignee visible even if they were removed from the roster
  if (form.assignee_id && !assigneeOptions.some(o => o.id === form.assignee_id)) {
    assigneeOptions.push({ id: form.assignee_id, name: form.assignee_name || 'Former assignee' });
  }

  const togglePlatform = (pl) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(pl) ? f.platforms.filter(x => x !== pl) : [...f.platforms, pl],
    }));
  };

  const onAssign = (id) => {
    const u = assigneeOptions.find(x => x.id === id);
    setForm(f => ({ ...f, assignee_id: id, assignee_name: u?.name || '' }));
  };

  // Chunked upload → object storage. Handles many files at once, up to 300 MB
  // each, with per-file progress.
  const uploadOne = async (f) => {
    const { data: init } = await api.post('/marketing/social/uploads/init', {
      filename: f.name, content_type: f.type || 'application/octet-stream', total_size: f.size,
    });
    let sent = 0;
    while (sent < f.size) {
      const blob = f.slice(sent, sent + UPLOAD_CHUNK_BYTES);
      const fd = new FormData();
      fd.append('upload_id', init.upload_id);
      fd.append('chunk', blob, 'chunk');
      await api.post('/marketing/social/uploads/chunk', fd);
      sent += blob.size;
      const pct = Math.round((sent / f.size) * 100);
      setUploads(u => u.map(x => x.name === f.name ? { ...x, pct } : x));
    }
    const { data: att } = await api.post('/marketing/social/uploads/complete', { upload_id: init.upload_id });
    return att;
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if ((form.attachments || []).length + files.length > MAX_ATTACHMENTS) {
      toast.error(`Max ${MAX_ATTACHMENTS} attachments per post`);
      return;
    }
    const valid = files.filter(f => {
      if (f.size > MAX_UPLOAD_BYTES) { toast.error(`${f.name} is over 300 MB`); return false; }
      return true;
    });
    setUploads(valid.map(f => ({ name: f.name, pct: 0 })));
    for (const f of valid) {
      try {
        const att = await uploadOne(f);
        setForm(prev => ({ ...prev, attachments: [...(prev.attachments || []), att] }));
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`);
      } finally {
        setUploads(u => u.filter(x => x.name !== f.name));
      }
    }
    if (valid.length) toast.success(`${valid.length} file${valid.length === 1 ? '' : 's'} uploaded`);
  };

  const removeAttachment = (idx) => {
    setForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const attachmentHref = (a) => {
    if (a.data_url) return a.data_url;
    if (a.storage_path && post.id) {
      const base = api.defaults?.baseURL || '';
      return `${base}/marketing/social/${post.id}/attachments/${a.id}`;
    }
    return '';
  };

  const save = async () => {
    if (!form.platforms.length) { toast.error('Pick at least one platform'); return; }
    setBusy(true);
    try {
      if (isNew) {
        await api.post('/marketing/social', form);
        toast.success('Post created');
      } else {
        await api.put(`/marketing/social/${post.id}`, form);
        toast.success('Saved');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/marketing/social/${post.id}`);
    toast.success('Deleted'); onSaved();
  };

  const addComment = async () => {
    const text = newComment.trim();
    if (!text || isNew) return;
    try {
      const { data } = await api.post(`/marketing/social/${post.id}/comments`, { text, private: true });
      setComments(c => [...c, data]);
      setNewComment('');
    } catch { toast.error('Comment failed'); }
  };

  const removeComment = async (cid) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/marketing/social/${post.id}/comments/${cid}`);
      setComments(c => c.filter(x => x.id !== cid));
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0" data-testid="mk-social-editor">
        <DialogHeader className="px-6 py-4 border-b flex-row items-center justify-between">
          <DialogTitle className="text-lg">{isNew ? 'New post' : 'Edit post'}</DialogTitle>
          {!isNew && (
            <Button variant="ghost" size="sm" onClick={del} className="text-red-600 hover:bg-red-50" data-testid="mk-social-editor-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto grid md:grid-cols-3 gap-0 min-h-0">
          {/* Main pane */}
          <div className="md:col-span-2 p-6 space-y-3 border-r">
            <Input value={form.title}
                   onChange={e => set('title', e.target.value)}
                   placeholder="Post title (e.g., Timely & Relevant: PA Day)"
                   className="text-lg font-semibold"
                   data-testid="mk-social-editor-title" />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Scheduled for</Label>
                <Input type="datetime-local" value={form.scheduled_date}
                       onChange={e => set('scheduled_date', e.target.value)}
                       data-testid="mk-social-editor-date" />
              </div>
              <div>
                <Label>Status</Label>
                <select value={form.status} onChange={e => set('status', e.target.value)}
                        className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white capitalize"
                        data-testid="mk-social-editor-status">
                  {SOCIAL_STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </select>
              </div>
              <div>
                <Label>Post type</Label>
                <select value={form.post_type} onChange={e => set('post_type', e.target.value)}
                        className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white"
                        data-testid="mk-social-editor-post-type">
                  <option value="">—</option>
                  <option value="photo">PHOTO</option>
                  <option value="video">VIDEO</option>
                  <option value="graphic">GRAPHIC</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Platforms *</Label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="mk-social-editor-platforms">
                {Object.entries(PLATFORM_META).map(([k, meta]) => {
                  const on = form.platforms.includes(k);
                  const Icon = meta.Icon;
                  return (
                    <button key={k} type="button" onClick={() => togglePlatform(k)}
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs transition-all ${on ? 'text-white' : 'text-gray-600 border bg-white'}`}
                            style={on ? { backgroundColor: meta.color } : {}}
                            data-testid={`mk-social-editor-platform-${k}`}>
                      {Icon && <Icon className="h-3 w-3" />}
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Assignee</Label>
              <select value={form.assignee_id} onChange={e => onAssign(e.target.value)}
                      className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white"
                      data-testid="mk-social-editor-assignee">
                <option value="">Unassigned</option>
                {assigneeOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {team.length > 0 && <p className="text-[10px] text-gray-400 mt-0.5">Showing your social team — manage it via the Team button.</p>}
            </div>
            <div>
              <Label>Caption</Label>
              <textarea rows={6} value={form.copy}
                        onChange={e => set('copy', e.target.value)}
                        placeholder="Post copy — the caption that goes on Facebook & Instagram."
                        className="w-full mt-1 rounded-xl border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
                        data-testid="mk-social-editor-copy" />
            </div>
            <div>
              <Label>Image URL <span className="text-[10px] text-gray-400 font-normal">(optional — for thumbnails)</span></Label>
              <Input value={form.image_url} onChange={e => set('image_url', e.target.value)}
                     placeholder="https://…" data-testid="mk-social-editor-image-url" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="mb-0">Attachments <span className="text-[10px] text-gray-400 font-normal">(Reels, graphics, photos, PDFs — select several at once, up to 300 MB each)</span></Label>
                <label className="text-xs px-2.5 py-1 rounded border hover:border-[#E91E63] hover:text-[#E91E63] cursor-pointer inline-flex items-center gap-1"
                       data-testid="mk-social-editor-attach-btn">
                  <Paperclip className="h-3 w-3" /> Add files
                  <input type="file" multiple hidden onChange={onFile} accept="image/*,video/*,application/pdf" />
                </label>
              </div>
              {uploads.length > 0 && (
                <div className="space-y-1 mb-2" data-testid="mk-social-editor-uploading">
                  {uploads.map(u => (
                    <div key={u.name} className="text-xs">
                      <div className="flex justify-between text-gray-600 mb-0.5">
                        <span className="truncate">{u.name}</span><span>{u.pct}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
                        <div className="h-full bg-[#E91E63] transition-all" style={{ width: `${u.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {(form.attachments || []).length === 0 && uploads.length === 0 ? (
                <div className="border-2 border-dashed rounded-lg py-4 text-center text-xs text-gray-400">
                  No files yet — attach Reels, graphics, or photos for the team to review
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="mk-social-editor-attachments">
                  {form.attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 border rounded bg-gray-50 text-xs">
                      {a.type?.startsWith('image/') && attachmentHref(a) ? (
                        <img src={attachmentHref(a)} alt="" className="h-8 w-8 object-cover rounded" />
                      ) : a.type?.startsWith('image/') ? (
                        <div className="h-8 w-8 rounded bg-gray-200 text-gray-500 flex items-center justify-center text-[9px]">IMG</div>
                      ) : a.type?.startsWith('video/') ? (
                        <div className="h-8 w-8 rounded bg-black text-white flex items-center justify-center text-[10px]">VIDEO</div>
                      ) : (
                        <FileText className="h-6 w-6 text-gray-500 shrink-0" />
                      )}
                      <span className="flex-1 truncate" title={!attachmentHref(a) && a.storage_path ? 'Preview & download available after you save the post' : a.name}>{a.name}</span>
                      <span className="text-gray-400">{(a.size || 0) > 1024 * 1024 ? `${((a.size || 0) / (1024 * 1024)).toFixed(1)} MB` : (a.size || 0) >= 1024 ? `${Math.round((a.size || 0) / 1024)} KB` : `${a.size || 0} B`}</span>
                      {attachmentHref(a) && (
                        <a href={attachmentHref(a)} download={a.name} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                           className="text-[#E91E63] hover:underline" title="Download">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      <button type="button" onClick={() => removeAttachment(i)}
                              className="text-gray-400 hover:text-red-600"
                              data-testid={`mk-social-editor-attach-remove-${i}`}>
                        <XIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Activity side pane — comments + history */}
          <div className="p-4 bg-gray-50 overflow-y-auto flex flex-col min-h-0">
            <div className="font-semibold text-sm mb-2 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-[#E91E63]" /> Activity
            </div>
            {isNew ? (
              <p className="text-xs text-gray-500 italic">Comments and history become available once the post is saved.</p>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2 mb-2 min-h-[200px]">
                {comments.map(c => (
                  <div key={c.id} className="bg-white rounded-lg p-2 border" data-testid={`mk-social-comment-${c.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-semibold">{c.by}</div>
                      <div className="flex items-center gap-2">
                        {c.private && <span className="text-[9px] uppercase tracking-wider text-[#6B3FA0]">private</span>}
                        <button onClick={() => removeComment(c.id)}
                                className="text-gray-300 hover:text-red-600" title="Delete note">
                          <XIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-800 whitespace-pre-wrap">{c.text}</div>
                    <div className="text-[9px] text-gray-400 mt-1">{new Date(c.at).toLocaleString()}</div>
                  </div>
                ))}
                {history.length > 0 && (
                  <div className="pt-2 mt-2 border-t">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <HistoryIcon className="h-3 w-3" /> History
                    </div>
                    {history.slice().reverse().map((h, i) => (
                      <div key={i} className="text-[10px] text-gray-500 py-0.5 leading-tight">
                        <span className="text-gray-700">{h.text}</span> · {new Date(h.at).toLocaleString()}
                      </div>
                    ))}
                  </div>
                )}
                {comments.length === 0 && history.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No activity yet.</p>
                )}
              </div>
            )}
            {!isNew && (
              <div className="flex gap-1 items-end pt-2 border-t">
                <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                          placeholder="Leave a note (private — @mention teammates)"
                          rows={2}
                          className="flex-1 rounded-lg border px-2 py-1.5 text-xs bg-white focus-visible:outline-none focus-visible:ring-1"
                          data-testid="mk-social-comment-input" />
                <Button size="sm" onClick={addComment} disabled={!newComment.trim()}
                        className="bg-[#E91E63] hover:bg-[#C2185B] text-white"
                        data-testid="mk-social-comment-send">
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="px-6 py-3 border-t">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy || uploads.length > 0} className="bg-[#E91E63] hover:bg-[#C2185B] text-white" data-testid="mk-social-editor-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : uploads.length > 0 ? 'Uploading…' : (isNew ? 'Create post' : 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamManager({ api, users, team, onClose, onSaved }) {
  const [members, setMembers] = useState(team.map(m => ({ ...m })));
  const [addId, setAddId] = useState('');
  const [busy, setBusy] = useState(false);

  const available = users.filter(u => !members.some(m => m.employee_id === u.id));

  const addMember = () => {
    if (!addId) return;
    const u = users.find(x => x.id === addId);
    if (!u) return;
    const used = new Set(members.map(m => m.color));
    const color = TEAM_COLORS.find(c => !used.has(c.hex))?.hex || TEAM_COLORS[0].hex;
    setMembers(m => [...m, { employee_id: u.id, name: u.name, color }]);
    setAddId('');
  };

  const setColor = (eid, hex) => setMembers(ms => ms.map(m => m.employee_id === eid ? { ...m, color: hex } : m));
  const remove = (eid) => setMembers(ms => ms.filter(m => m.employee_id !== eid));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put('/marketing/social/team', { members });
      toast.success('Social team saved');
      onSaved(data.members);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mk-social-team-dialog">
        <DialogHeader>
          <DialogTitle>Social team & colors</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-500 -mt-2">
          Pick who shows up as an assignee (3–4 people works best) and give each a color —
          cards, board columns, and the table tint by owner, just like your Rella board.
        </p>
        <div className="space-y-2">
          {members.map(m => (
            <div key={m.employee_id} className="flex items-center gap-2 p-2 border rounded-lg"
                 style={{ backgroundColor: `${m.color}14` }} data-testid={`mk-team-member-${m.employee_id}`}>
              <span className="h-4 w-4 rounded-full shrink-0 border" style={{ backgroundColor: m.color }} />
              <span className="flex-1 text-sm font-medium">{m.name}</span>
              <div className="flex gap-1">
                {TEAM_COLORS.map(c => (
                  <button key={c.hex} type="button" title={c.name}
                          onClick={() => setColor(m.employee_id, c.hex)}
                          className={`h-4 w-4 rounded-full border-2 transition-transform hover:scale-110 ${m.color === c.hex ? 'border-gray-800' : 'border-transparent'}`}
                          style={{ backgroundColor: c.hex }}
                          data-testid={`mk-team-color-${m.employee_id}-${c.name.toLowerCase()}`} />
                ))}
              </div>
              <button onClick={() => remove(m.employee_id)} className="text-gray-400 hover:text-red-600"
                      data-testid={`mk-team-remove-${m.employee_id}`}>
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          {members.length === 0 && <p className="text-xs text-gray-400 italic">No team members yet — everyone shows as an assignee until you add some.</p>}
        </div>
        <div className="flex gap-2">
          <select value={addId} onChange={e => setAddId(e.target.value)}
                  className="flex-1 border rounded px-2 py-1.5 text-sm bg-white" data-testid="mk-team-add-select">
            <option value="">Add a team member…</option>
            {available.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <Button variant="outline" onClick={addMember} disabled={!addId || members.length >= 8} data-testid="mk-team-add-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#E91E63] hover:bg-[#C2185B] text-white" data-testid="mk-team-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
